import {
  Button,
  Flex,
  Box,
  Text,
  Card,
  AlertDialog,
  Callout,
  IconButton,
  Tabs,
} from "@radix-ui/themes";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useMemo, useState, useEffect } from "react";
import { Info, CheckCircle2, ExternalLink, Copy, User as UserIcon, Layers } from "lucide-react";
import SearchableSelectField from "@/components/form/SearchableSelectField";
import InputField from "@/components/form/InputField";
import DynamicReferenceField from "@/components/form/DynamicReferenceField";
import PageTemplate from "@/components/page-template/PageTemplate";
import {
  CreateSubscriptionRequest,
  formatCurrency,
} from "@/types/breeze/AnythinkPay";
import { useAuth } from "@/providers/AuthProvider";
import { useCreateAnythinkPaySubscription } from "@/hooks/useAnythinkPaySubscriptions";
import { useUsers, useEntities } from "@/hooks/useCommonQueries";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";

type SubscriberMode = "user" | "entity";

interface AddSubscriberFormValues {
  mode: SubscriberMode;
  plan_id: string;
  user_id: string;
  subscriber_entity_id: number | "";
  subscriber_id: number | "";
  customer_email: string;
  customer_name: string;
  customer_phone: string;
}

const validationSchema = Yup.object({
  plan_id: Yup.string().required("Pick a plan"),
  mode: Yup.mixed<SubscriberMode>().oneOf(["user", "entity"]).required(),
  user_id: Yup.string().when("mode", {
    is: "user",
    then: (s) => s.required("Pick a user to subscribe"),
  }),
  subscriber_entity_id: Yup.mixed().when("mode", {
    is: "entity",
    then: () =>
      Yup.number().typeError("Pick an entity").required("Pick an entity"),
  }),
  subscriber_id: Yup.mixed().when("mode", {
    is: "entity",
    then: () =>
      Yup.number().typeError("Pick a record").required("Pick a record"),
  }),
  customer_email: Yup.string().when("mode", {
    is: "entity",
    then: (s) =>
      s
        .required("Customer email is required")
        .email("Invalid email format"),
  }),
  customer_phone: Yup.string().notRequired(),
});

// Clear the inactive mode's fields so stale values don't leak into the payload.
function ModeFieldReset() {
  const { values, setFieldValue } = useFormikContext<AddSubscriberFormValues>();
  useEffect(() => {
    if (values.mode === "user") {
      if (values.subscriber_entity_id !== "")
        setFieldValue("subscriber_entity_id", "");
      if (values.subscriber_id !== "") setFieldValue("subscriber_id", "");
      if (values.customer_email !== "") setFieldValue("customer_email", "");
      if (values.customer_name !== "") setFieldValue("customer_name", "");
    } else if (values.user_id !== "") {
      setFieldValue("user_id", "");
    }
  }, [values.mode]);
  return null;
}

export default function CreateSubscriptionPage() {
  const { safeNavigate, currentTenant } = useAuth();
  const createSubscription = useCreateAnythinkPaySubscription();
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [checkoutData, setCheckoutData] = useState<{
    checkout_url: string;
    session_id: string;
    anythink_pay_id: string;
  } | null>(null);

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: () => anythinkPayService.getSubscriptionPlans(),
  });

  const { data: users, isLoading: usersLoading } = useUsers();
  const { data: entities } = useEntities();

  const userEntityId = useMemo(
    () => entities?.find((e) => e.name === "user")?.id,
    [entities],
  );

  const activePlans = useMemo(
    () => (plans ?? []).filter((p) => p.is_active),
    [plans],
  );

  const planOptions = useMemo(
    () =>
      activePlans.map((p) => ({
        value: String(p.id),
        label: `${p.plan_name} — ${formatCurrency(p.amount, p.currency)} / ${p.interval_count} ${p.billing_interval}(s)`,
      })),
    [activePlans],
  );

  const userOptions = useMemo(
    () =>
      (users ?? []).map((u) => {
        const fullName = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
        return {
          value: String(u.id),
          label: fullName ? `${fullName} — ${u.email}` : u.email,
        };
      }),
    [users],
  );

  const handleSubmit = async (values: AddSubscriberFormValues) => {
    const plan = activePlans.find((p) => String(p.id) === values.plan_id);
    if (!plan) {
      toast.error("Selected plan is no longer active. Pick another plan.");
      return;
    }

    let subscriberEntityId: number | undefined;
    let subscriberId: number | undefined;
    let customerEmail: string;
    let customerName: string | undefined;

    if (values.mode === "user") {
      const user = (users ?? []).find((u) => String(u.id) === values.user_id);
      if (!user) {
        toast.error("Selected user could not be found.");
        return;
      }
      subscriberEntityId = userEntityId;
      subscriberId = user.id;
      customerEmail = user.email;
      const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
      customerName = fullName || undefined;
    } else {
      subscriberEntityId =
        values.subscriber_entity_id === ""
          ? undefined
          : Number(values.subscriber_entity_id);
      subscriberId =
        values.subscriber_id === "" ? undefined : Number(values.subscriber_id);
      customerEmail = values.customer_email;
      customerName = values.customer_name || undefined;
    }

    const subscriptionData: CreateSubscriptionRequest = {
      name: plan.name,
      description: plan.description || "",
      type: plan.type,
      currency: plan.currency,
      amount: plan.amount,
      billing_interval: plan.billing_interval,
      interval_count: plan.interval_count,
      trial_period_days: plan.trial_period_days ?? undefined,
      product_name: plan.product_name || undefined,
      product_description: plan.product_description || undefined,
      subscriber_entity_id: subscriberEntityId,
      subscriber_id: subscriberId,
      customer_email: customerEmail,
      customer_name: customerName,
      customer_phone: values.customer_phone || undefined,
      success_url:
        currentTenant?.tenant_settings?.payment_success_url ||
        `${window.location.origin}/org/${currentTenant?.id}/integrations/anythinkpay/subscriptions/success`,
      cancel_url:
        currentTenant?.tenant_settings?.payment_cancel_url ||
        `${window.location.origin}/org/${currentTenant?.id}/integrations/anythinkpay/subscriptions/cancelled`,
    };

    createSubscription.mutate(subscriptionData, {
      onSuccess: (response: any) => {
        if (response.checkout_url) {
          setCheckoutData({
            checkout_url: response.checkout_url,
            session_id: response.session_id,
            anythink_pay_id: response.anythink_pay_id,
          });
          setShowCheckoutDialog(true);
        } else {
          safeNavigate(`/integrations/anythinkpay/subscriptions`);
        }
      },
    });
  };

  const handleCancel = () => {
    safeNavigate(`/integrations/anythinkpay/subscriptions`);
  };

  const handleOpenCheckout = () => {
    if (checkoutData?.checkout_url) window.open(checkoutData.checkout_url, "_blank");
  };

  const handleCopyCheckoutUrl = () => {
    if (!checkoutData?.checkout_url) return;
    navigator.clipboard.writeText(checkoutData.checkout_url);
    toast.success("Checkout URL copied to clipboard");
  };

  const handleCloseCheckoutDialog = () => {
    setShowCheckoutDialog(false);
    setCheckoutData(null);
    safeNavigate(`/integrations/anythinkpay/subscriptions`);
  };

  const noPlans = !plansLoading && activePlans.length === 0;

  return (
    <PageTemplate
      title="Add Subscriber"
      description="Subscribe an existing user (or any other entity record) to one of your plans. They'll receive a Stripe Checkout link to enter payment details."
    >
      {noPlans ? (
        <Callout.Root color="amber">
          <Callout.Icon>
            <Info size={16} />
          </Callout.Icon>
          <Callout.Text>
            You don't have any active plans yet.{" "}
            <a
              href={`/org/${currentTenant?.id}/integrations/anythinkpay/subscriptions/plans/create`}
              style={{ color: "var(--accent-9)" }}
            >
              Create one
            </a>{" "}
            before adding a subscriber.
          </Callout.Text>
        </Callout.Root>
      ) : (
        <Formik<AddSubscriberFormValues>
          initialValues={{
            mode: "user",
            plan_id: "",
            user_id: "",
            subscriber_entity_id: "",
            subscriber_id: "",
            customer_email: "",
            customer_name: "",
            customer_phone: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => {
            const selectedPlan = activePlans.find(
              (p) => String(p.id) === values.plan_id,
            );
            const selectedUser =
              values.mode === "user"
                ? (users ?? []).find((u) => String(u.id) === values.user_id)
                : undefined;
            const canSubmit =
              !!selectedPlan &&
              (values.mode === "user"
                ? !!selectedUser
                : values.subscriber_entity_id !== "" &&
                  values.subscriber_id !== "" &&
                  values.customer_email.length > 0);

            return (
              <Form>
                <ModeFieldReset />
                <Flex direction="column" gap="5" style={{ maxWidth: 640 }}>
                  <Box>
                    <Text size="2" weight="medium" as="label" mb="2">
                      Subscriber
                    </Text>
                    <Tabs.Root
                      value={values.mode}
                      onValueChange={(v) =>
                        setFieldValue("mode", v as SubscriberMode)
                      }
                    >
                      <Tabs.List mb="3">
                        <Tabs.Trigger value="user">
                          <Flex align="center" gap="2">
                            <UserIcon size={14} /> User
                          </Flex>
                        </Tabs.Trigger>
                        <Tabs.Trigger value="entity">
                          <Flex align="center" gap="2">
                            <Layers size={14} /> Other entity
                          </Flex>
                        </Tabs.Trigger>
                      </Tabs.List>

                      <Tabs.Content value="user">
                        <SearchableSelectField
                          name="user_id"
                          label="User"
                          required
                          options={userOptions}
                          placeholder={
                            usersLoading ? "Loading users..." : "Pick a user..."
                          }
                          searchPlaceholder="Search by name or email"
                          description="Existing Anythink user this subscription will be linked to. They'll be the Stripe customer."
                        />
                      </Tabs.Content>

                      <Tabs.Content value="entity">
                        <DynamicReferenceField
                          entityFieldName="subscriber_entity_id"
                          recordFieldName="subscriber_id"
                          label="Subscriber record"
                          required
                          description="Pick any entity and the specific record to subscribe. Useful when the subscriber isn't a user (e.g. a Workspace or Team)."
                        />
                        <InputField
                          name="customer_email"
                          label="Customer email"
                          type="email"
                          required
                          description="Where Stripe will send receipts and the checkout link."
                        />
                        <InputField
                          name="customer_name"
                          label="Customer name"
                          type="text"
                          description="Shown on receipts and in Stripe."
                        />
                      </Tabs.Content>
                    </Tabs.Root>
                  </Box>

                  <SearchableSelectField
                    name="plan_id"
                    label="Plan"
                    required
                    options={planOptions}
                    placeholder="Pick a plan..."
                    searchPlaceholder="Search plans"
                    description="The pricing, billing interval and trial period come from the selected plan."
                  />

                  <InputField
                    name="customer_phone"
                    label="Customer phone (optional)"
                    type="tel"
                    description="Optional phone number passed to Stripe with the customer record."
                  />

                  {selectedPlan && (selectedUser || values.mode === "entity") && (
                    <Card>
                      <Box p="4">
                        <Text size="2" color="gray" as="p" mb="3">
                          Summary
                        </Text>
                        <Flex direction="column" gap="2">
                          <Flex justify="between">
                            <Text size="2" color="gray">
                              Subscriber
                            </Text>
                            <Text size="2" weight="medium">
                              {values.mode === "user"
                                ? selectedUser?.email
                                : values.customer_email || "—"}
                            </Text>
                          </Flex>
                          <Flex justify="between">
                            <Text size="2" color="gray">
                              Plan
                            </Text>
                            <Text size="2" weight="medium">
                              {selectedPlan.plan_name}
                            </Text>
                          </Flex>
                          <Flex justify="between">
                            <Text size="2" color="gray">
                              Amount
                            </Text>
                            <Text size="2" weight="medium">
                              {formatCurrency(
                                selectedPlan.amount,
                                selectedPlan.currency,
                              )}{" "}
                              every {selectedPlan.interval_count}{" "}
                              {selectedPlan.billing_interval}(s)
                            </Text>
                          </Flex>
                          {selectedPlan.trial_period_days ? (
                            <Flex justify="between">
                              <Text size="2" color="gray">
                                Trial
                              </Text>
                              <Text size="2" weight="medium">
                                {selectedPlan.trial_period_days} days
                              </Text>
                            </Flex>
                          ) : null}
                        </Flex>
                      </Box>
                    </Card>
                  )}

                  <Flex gap="2" justify="end">
                    <Button type="button" variant="outline" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={createSubscription.isPending}
                      disabled={!canSubmit}
                    >
                      Send checkout link
                    </Button>
                  </Flex>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      )}

      <AlertDialog.Root
        open={showCheckoutDialog}
        onOpenChange={setShowCheckoutDialog}
      >
        <AlertDialog.Content style={{ maxWidth: 480 }}>
          <Flex direction="column" gap="3">
            <Flex align="center" gap="2">
              <CheckCircle2 size={20} color="var(--green-9)" />
              <AlertDialog.Title>Subscription created</AlertDialog.Title>
            </Flex>
            <AlertDialog.Description>
              <Text as="p" size="2" color="gray">
                Open the checkout link to enter payment details, or copy it to share with the subscriber.
              </Text>
            </AlertDialog.Description>
            <Flex gap="2">
              <Button onClick={handleOpenCheckout}>
                <ExternalLink size={14} /> Open checkout
              </Button>
              <IconButton variant="soft" onClick={handleCopyCheckoutUrl}>
                <Copy size={14} />
              </IconButton>
            </Flex>
            <Flex gap="2" justify="end">
              <Button variant="soft" onClick={handleCloseCheckoutDialog}>
                Done
              </Button>
            </Flex>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </PageTemplate>
  );
}
